<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'
import { useVModel } from '@vueuse/core'
import { cn } from '@ds/lib/utils'

/*
 * Sizes mirror Input's `sm | md | lg`, but scale padding rather than height —
 * a textarea's box is driven by `rows`, so a fixed height would fight it.
 *
 * `md` is the canonical component: 10px/12px padding at 16px. The other two are
 * (derived) — the canonical Textarea publishes no size variants. Each steps the
 * vertical padding with the type size, which keeps a one-row textarea within a
 * couple of pixels of the Input of the same name. Horizontal padding stays 12px
 * across all three, exactly as Input does.
 */
const SIZE_CLASS = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-3 py-2.5 text-base',
  lg: 'px-3 py-3 text-lg',
} as const

// A textarea's value is always a string, so — unlike Input, which also serves
// number fields — the model is narrowed to one.
const props = defineProps<{
  defaultValue?: string
  modelValue?: string
  class?: HTMLAttributes['class']
  size?: keyof typeof SIZE_CLASS
  rows?: number
}>()

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true,
  defaultValue: props.defaultValue,
})

const textareaSizeClass = computed(() => SIZE_CLASS[props.size ?? 'md'])
</script>

<template>
  <textarea
    v-model="modelValue"
    :rows="props.rows ?? 4"
    :class="cn(
      // `ds-control` opts this element out of @tailwindcss/forms' base styles,
      // most importantly its :focus ring. See src/assets/styles/ds-base.css,
      // which also supplies the design system's Iris focus ring.
      'ds-control',
      'flex w-full resize-y',
      // Padding and type size only — unlike Input there is no fixed height to
      // drive the vertical rhythm from, so padding does it here.
      textareaSizeClass,
      'bg-card text-foreground placeholder:text-foreground/35',
      // Inter at Medium (500) — the design system's UI workhorse weight.
      'font-ds font-medium leading-normal',
      'border border-input rounded-ds-md',
      'transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard',
      // Canonical disabled state is the sunken surface, not reduced opacity.
      'disabled:bg-muted disabled:cursor-not-allowed',
      props.class,
    )"
  />
</template>
